/**
 * Poller Bounding Box layer for displaying distributed poller coverage areas on the map.
 * Shows poller name and live vehicle count within each bounding box.
 *
 * Visual encoding:
 * - Color by poller type: V4 = blue, V1-DA = purple
 * - Status by line dash: solid (active), dashed (stale), dotted (offline)
 */

import { memo, useMemo } from 'react';
import { Source, Layer } from 'react-map-gl/maplibre';
import { useQuery } from '@tanstack/react-query';
import type { FillLayerSpecification, LineLayerSpecification, SymbolLayerSpecification } from 'maplibre-gl';
import type { FeatureCollection, Point } from 'geojson';

import { useSettingsStore } from '../../stores/settingsStore';
import { useVehicleStore } from '../../stores/vehicleStore';
import { getPollerBboxes, queryKeys } from '../../services/api';
import { POLLER_COLORS } from '../../config';
import type { PollerBboxFeatureCollection } from '../../types/api';

// Data-driven color expression matching on poller_type
const POLLER_TYPE_COLOR_EXPR = [
  'match',
  ['get', 'poller_type'],
  'v4',
  POLLER_COLORS.v4,
  'v1da',
  POLLER_COLORS.v1da,
  POLLER_COLORS.v4, // default
] as unknown as string;

// Static IDs - must be consistent across renders to prevent duplicate layers
const BBOX_SOURCE_ID = 'poller-bbox-source';
const BBOX_LABEL_SOURCE_ID = 'poller-bbox-label-source';
const BBOX_FILL_LAYER_ID = 'poller-bbox-fill-layer';
const BBOX_LINE_ACTIVE_LAYER_ID = 'poller-bbox-line-active';
const BBOX_LINE_STALE_LAYER_ID = 'poller-bbox-line-stale';
const BBOX_LINE_OFFLINE_LAYER_ID = 'poller-bbox-line-offline';
const BBOX_LABEL_LAYER_ID = 'poller-bbox-label-layer';

/**
 * Extract the top-right (northeast) corner from a polygon's coordinates.
 * For GeoJSON polygon, coordinates[0] is the outer ring.
 * The top-right corner is the point with maximum longitude and maximum latitude.
 */
function getTopRightCorner(coordinates: number[][][]): [number, number] {
  const ring = coordinates[0];
  let maxLon = -Infinity;
  let maxLat = -Infinity;

  for (const [lon, lat] of ring) {
    if (lon > maxLon) maxLon = lon;
    if (lat > maxLat) maxLat = lat;
  }

  return [maxLon, maxLat];
}

/**
 * Count vehicles within a bounding box.
 */
function countVehiclesInBbox(
  vehicles: Map<string, { latitude: number; longitude: number; targetLatitude: number; targetLongitude: number }>,
  minLat: number,
  maxLat: number,
  minLon: number,
  maxLon: number
): number {
  let count = 0;
  vehicles.forEach((vehicle) => {
    // Use target position (latest known position)
    const lat = vehicle.targetLatitude;
    const lon = vehicle.targetLongitude;
    if (lat >= minLat && lat <= maxLat && lon >= minLon && lon <= maxLon) {
      count++;
    }
  });
  return count;
}

interface LabelProperties {
  label: string;
  vehicle_count: number;
  poller_type: string;
}

export const PollerBboxLayer = memo(function PollerBboxLayer() {
  const pollerBboxEnabled = useSettingsStore((state) => state.pollerBboxEnabled);
  const vehicles = useVehicleStore((state) => state.vehicles);

  // Fetch poller bboxes from API
  const { data: bboxData } = useQuery<PollerBboxFeatureCollection>({
    queryKey: queryKeys.pollerBboxes.all,
    queryFn: () => getPollerBboxes(),
    enabled: pollerBboxEnabled,
    staleTime: 30_000, // 30 seconds - pollers are more dynamic than grids
    refetchInterval: 30_000,
  });

  // Use fetched data or empty feature collection for polygons
  // Strip 'name' property to prevent CartoCDN Voyager auto-labeling
  const polygonGeojson = useMemo(() => {
    if (!pollerBboxEnabled || !bboxData) {
      return { type: 'FeatureCollection' as const, features: [] };
    }
    return {
      type: 'FeatureCollection' as const,
      features: bboxData.features.map((feature) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { name, ...restProperties } = feature.properties;
        return {
          ...feature,
          properties: restProperties,
        };
      }),
    };
  }, [pollerBboxEnabled, bboxData]);

  // Create point features for labels at top-right corners with vehicle counts
  const labelGeojson: FeatureCollection<Point, LabelProperties> = useMemo(() => {
    if (!pollerBboxEnabled || !bboxData) {
      return { type: 'FeatureCollection', features: [] };
    }

    const features = bboxData.features.map((feature) => {
      const topRight = getTopRightCorner(feature.geometry.coordinates);
      const { min_lat, max_lat, min_lon, max_lon, poller_id, status, poller_type } = feature.properties;

      // Count vehicles in this poller's bbox
      const vehicleCount = countVehiclesInBbox(vehicles, min_lat, max_lat, min_lon, max_lon);

      return {
        type: 'Feature' as const,
        geometry: {
          type: 'Point' as const,
          coordinates: topRight,
        },
        properties: {
          label: `${poller_id} [${status}] (${vehicleCount})`,
          vehicle_count: vehicleCount,
          poller_type: poller_type,
        },
      };
    });

    return { type: 'FeatureCollection', features };
  }, [pollerBboxEnabled, bboxData, vehicles]);

  // Fill layer style - 5% opaque, color based on poller_type
  const fillLayerStyle: FillLayerSpecification = useMemo(
    () => ({
      id: BBOX_FILL_LAYER_ID,
      type: 'fill',
      source: BBOX_SOURCE_ID,
      paint: {
        'fill-color': POLLER_TYPE_COLOR_EXPR,
        'fill-opacity': 0.05,
      },
    }),
    []
  );

  // Line layers split by status for different dash patterns
  // (MapLibre doesn't support data-driven line-dasharray)
  const lineActiveStyle: LineLayerSpecification = useMemo(
    () => ({
      id: BBOX_LINE_ACTIVE_LAYER_ID,
      type: 'line',
      source: BBOX_SOURCE_ID,
      filter: ['==', ['get', 'status'], 'active'],
      paint: {
        'line-color': POLLER_TYPE_COLOR_EXPR,
        'line-width': 2,
        'line-opacity': 0.7,
      },
    }),
    []
  );

  const lineStaleStyle: LineLayerSpecification = useMemo(
    () => ({
      id: BBOX_LINE_STALE_LAYER_ID,
      type: 'line',
      source: BBOX_SOURCE_ID,
      filter: ['==', ['get', 'status'], 'stale'],
      paint: {
        'line-color': POLLER_TYPE_COLOR_EXPR,
        'line-width': 2,
        'line-opacity': 0.7,
        'line-dasharray': [8, 4],
      },
    }),
    []
  );

  const lineOfflineStyle: LineLayerSpecification = useMemo(
    () => ({
      id: BBOX_LINE_OFFLINE_LAYER_ID,
      type: 'line',
      source: BBOX_SOURCE_ID,
      filter: ['==', ['get', 'status'], 'offline'],
      paint: {
        'line-color': POLLER_TYPE_COLOR_EXPR,
        'line-width': 2,
        'line-opacity': 0.7,
        'line-dasharray': [2, 4],
      },
    }),
    []
  );

  // Label layer style - poller name with status and vehicle count at top-right corner
  const labelLayerStyle: SymbolLayerSpecification = useMemo(
    () => ({
      id: BBOX_LABEL_LAYER_ID,
      type: 'symbol',
      source: BBOX_LABEL_SOURCE_ID,
      layout: {
        'text-field': ['get', 'label'],
        'text-font': ['Noto Sans Regular'],
        'text-size': 12,
        'text-anchor': 'top-right',
        'text-offset': [-0.3, 0.3],
        'text-allow-overlap': true,
      },
      paint: {
        'text-color': POLLER_TYPE_COLOR_EXPR,
        'text-opacity': 1,
        'text-halo-color': '#ffffff',
        'text-halo-width': 2,
      },
    }),
    []
  );

  return (
    <>
      <Source id={BBOX_SOURCE_ID} type="geojson" data={polygonGeojson}>
        <Layer {...fillLayerStyle} />
        <Layer {...lineActiveStyle} />
        <Layer {...lineStaleStyle} />
        <Layer {...lineOfflineStyle} />
      </Source>
      <Source id={BBOX_LABEL_SOURCE_ID} type="geojson" data={labelGeojson}>
        <Layer {...labelLayerStyle} />
      </Source>
    </>
  );
});
